'use client';
import Image from 'next/image';
import { FaAngleDown } from 'react-icons/fa';
import { useState } from 'react';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselPrevious,
    CarouselNext,
} from '@/components/ui/carousel';
import { FaAnglesLeft, FaAnglesRight } from 'react-icons/fa6';
import { GoDotFill } from 'react-icons/go';
import CentersCard from './centers-card';
import { cn } from '@/lib/utils';
import Link from 'next/link';

const MedicalCenters = ({
    data,
}: {
    data: {
        title: string;
        list: [
            {
                id: number;
                home_section_id: number;
                title: string;
                sub_title: string;
                pic: string;
                link: string;
                slug: string;
                count: number;
                centers: [
                    {
                        id: number;
                        name: string;
                        role: string;
                        city: {
                            name: string;
                            slug: string;
                        };
                        pic: string;
                    },
                ];
            },
        ];
    };
}) => {
    const [type, setType] = useState(
        data.list[0].slug ? data.list[0].slug : '',
    );
    return (
        <div className="xl:!px-0 px-3">
            <p className="text-2xl font-bold text-center w-full">
                {data.title}
            </p>
            <hr className="mt-2 w-full" />
            <section className="md:!flex hidden grid-cols-2 flex-wrap justify-center gap-y-14 gap-4 pt-16 mb-20">
                {data.list.map((obj) => (
                    <div
                        key={obj.id}
                        onClick={() => setType(obj.slug)}
                        className={`${
                            obj.slug === type
                                ? 'bg-blue text-white'
                                : 'bg-white hover:bg-blue hover:text-white'
                        } relative h-[140px] cursor-pointer transition-colors smp:!px-6 py-6 px-3 w-full smp:!w-[200px] rounded-2xl shadow-m`}
                    >
                        <div className="absolute -top-[40px] p-3 left-1/2 -translate-x-1/2 border-4 border-[#f5faff] size-[70px] rounded-full flex items-center justify-center bg-white overflow-hidden">
                            <Image
                                unoptimized
                                src={
                                    obj.pic
                                        ? process.env.NEXT_PUBLIC_STATIC_URL +
                                          obj.pic
                                        : ''
                                }
                                width={1000}
                                height={1000}
                                alt={obj.title}
                                className="size-[64px] object-contain"
                            />
                        </div>
                        <div className="pt-[20px] text-center">
                            <p className="sm:!text-[18px] text-base font-semibold">
                                {obj.title}
                            </p>
                            <span className="block text-sm font-normal pt-2">
                                {obj.count} {obj.title}
                            </span>
                            <FaAngleDown className="text-xl mx-auto mt-3" />
                        </div>
                    </div>
                ))}
            </section>

            <Carousel
                opts={{
                    direction: 'rtl',
                }}
                className="!w-full mx-auto !px-8 md:!hidden smp:!px-10 py-10 mt-2"
            >
                <CarouselContent className="py-10 gap-3">
                    {data.list.map((obj) => (
                        <CarouselItem
                            key={`${obj.id}-${obj.slug}`}
                            style={{
                                flexBasis: 'auto',
                            }}
                        >
                            <div
                                key={obj.id}
                                onClick={() => setType(obj.slug)}
                                className={`${
                                    obj.slug === type
                                        ? 'bg-blue text-white'
                                        : 'bg-white hover:bg-blue hover:text-white'
                                } relative h-[140px] cursor-pointer transition-colors smp:!px-6 py-6 px-3 w-[200px] rounded-2xl shadow-m`}
                            >
                                <div className="absolute -top-[40px] p-3 left-1/2 -translate-x-1/2 border-4 border-[#f5faff] size-[70px] rounded-full flex items-center justify-center bg-white overflow-hidden">
                                    <Image
                                        unoptimized
                                        src={
                                            obj.pic
                                                ? process.env
                                                      .NEXT_PUBLIC_STATIC_URL +
                                                  obj.pic
                                                : ''
                                        }
                                        width={1000}
                                        height={1000}
                                        alt={obj.title}
                                        className="size-[64px] object-contain"
                                    />
                                </div>
                                <div className="pt-[20px] text-center">
                                    <p className="sm:!text-[18px] text-base font-semibold">
                                        {obj.title}
                                    </p>
                                    <span className="block text-sm font-normal pt-2">
                                        {obj.count} {obj.title}
                                    </span>
                                    <FaAngleDown className="text-xl mx-auto mt-3" />
                                </div>
                            </div>
                        </CarouselItem>
                    ))}
                </CarouselContent>
                <CarouselPrevious
                    icon={<FaAnglesLeft />}
                    className="text-[#222222] hover:!text-[#222222] !bg-transparent !shadow-none hover:!opacity-50 transition-opacity !text-base smp:!text-[1.4rem] !border-0 left-0 disabled:!opacity-50"
                    invert
                />
                <CarouselNext
                    icon={<FaAnglesRight />}
                    className="text-[#222222] !bg-transparent !shadow-none hover:!text-[#222222] hover:!opacity-50 transition-opacity !text-base smp:!text-[1.4rem] !border-0 right-0 disabled:!opacity-50"
                    invert
                />
            </Carousel>

            <div className="bg-lavender relative py-6 rounded-2xl border-[3px] border-white">
                <p className="absolute -top-8 right-1/2 whitespace-nowrap smp:!translate-x-0 translate-x-1/2 smp:!right-8 text-xl xs:!text-2xl font-bold bg-[#f3f3f9] p-3 rounded-xl">
                    {data.list.find((mc) => mc.slug === type)?.title} های برتر
                </p>
                <div className="w-full md:!px-6">
                    <Carousel
                        opts={{
                            direction: 'rtl',
                        }}
                        className="!w-full mx-auto !px-8 smp:!px-10 mt-2"
                    >
                        <CarouselContent className="py-4">
                            {data.list.map((typeItem) =>
                                typeItem.centers.map((item) => (
                                    <CarouselItem
                                        key={`${item.id}-${typeItem.slug}`}
                                        style={{
                                            flexBasis: 'auto',
                                            paddingLeft:
                                                typeItem.slug !== type
                                                    ? 0
                                                    : undefined,
                                        }}
                                    >
                                        <CentersCard
                                            data={item}
                                            className={cn(
                                                'md:!w-[320px] sm:!w-[250px] w-[210px]',
                                                typeItem.slug !== type &&
                                                    'hidden',
                                            )}
                                        />
                                    </CarouselItem>
                                )),
                            )}
                        </CarouselContent>
                        <CarouselPrevious
                            icon={<FaAnglesLeft />}
                            className="text-[#222222] hover:!text-[#222222] !bg-transparent !shadow-none hover:!opacity-50 transition-opacity !text-base smp:!text-[1.4rem] !border-0 left-0 disabled:!opacity-50"
                            invert
                        />
                        <CarouselNext
                            icon={<FaAnglesRight />}
                            className="text-[#222222] !bg-transparent !shadow-none hover:!text-[#222222] hover:!opacity-50 transition-opacity !text-base smp:!text-[1.4rem] !border-0 right-0 disabled:!opacity-50"
                            invert
                        />
                    </Carousel>

                    <div className="smp:!px-8 px-3">
                        <div className="px-4 py-2.5 flex justify-between items-center rounded-2xl text-white mt-3 bg-blue">
                            <p className="font-medium flex items-center">
                                <GoDotFill className="!text-2xl text-red-500 animate-pulse duration-1000" />
                                <span className="font-bold ml-1">
                                    {
                                        data.list.find((mc) => mc.slug === type)
                                            ?.count
                                    }
                                </span>{' '}
                                {
                                    data.list.find((mc) => mc.slug === type)
                                        ?.title
                                }{' '}
                            </p>
                            <Link
                                href={`/centers/type/${
                                    data.list.find((mc) => mc.slug === type)
                                        ?.slug
                                }`}
                                className="font-semibold hover:opacity-65 transition-opacity"
                            >
                                مشاهده همه
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MedicalCenters;
